import type { DisplayEntry, RowType } from './display.ts'

export interface Anchor { ref: string; offset: number }
export interface PositionedEntry { entry: DisplayEntry; top: number; height: number; index: number }
export interface Geometry {
  items: readonly PositionedEntry[]
  byRef: ReadonlyMap<string, PositionedEntry>
  height: number
}

/** CSS uses these same finite heights: text is clipped, metadata drops whole, never wraps unpredictably. */
export function rowHeight(type: RowType, width: number, fontSize: number) {
  const scale = fontSize / 16
  const inset = width < 360 ? 20 : 24
  return Math.ceil(Math.max(72, inset + (type === 'one-line' ? 24 : type === 'result' ? 72 : 40) * scale))
}
export function buildGeometry(entries: readonly DisplayEntry[], options: { width: number; fontSize: number }): Geometry {
  let height = 0
  const byRef = new Map<string, PositionedEntry>()
  const items = entries.map((entry, index) => {
    const size = entry.kind === 'group' ? Math.ceil(Math.max(32, options.fontSize * 1.25 + 12)) : rowHeight(entry.type, options.width, options.fontSize)
    const item = { entry, index, top: height, height: size }
    height += size
    if (entry.kind === 'row') byRef.set(entry.ref, item)
    return item
  })
  return { items, byRef, height }
}
function firstAt(geometry: Geometry, position: number) {
  let lo = 0, hi = geometry.items.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    const item = geometry.items[mid]
    if (item.top + item.height <= position) lo = mid + 1
    else hi = mid
  }
  return lo
}
/** Scroll work is a binary search plus the visible/overscan range, independent of loaded row count. */
export function windowAt(geometry: Geometry, top: number, height: number, overscan = 4) {
  const visibleFirst = firstAt(geometry, Math.max(0, top))
  const bottom = Math.max(0, top) + Math.max(0, height)
  const boundary = firstAt(geometry, bottom)
  const visibleLast = height > 0 ? Math.min(geometry.items.length, boundary + (geometry.items[boundary]?.top < bottom ? 1 : 0)) : visibleFirst
  const first = Math.max(0, visibleFirst - overscan)
  const last = Math.min(geometry.items.length, visibleLast + overscan)
  return { first, last, visibleFirst, visibleLast, items: geometry.items.slice(first, last) }
}
export function anchorAt(geometry: Geometry, top: number): Anchor | undefined {
  for (let index = firstAt(geometry, Math.max(0, top)); index < geometry.items.length; index++) {
    const item = geometry.items[index]
    if (item.entry.kind === 'row') return { ref: item.entry.ref, offset: top - item.top }
  }
  const last = geometry.items.at(-1)
  return last?.entry.kind === 'row' ? { ref: last.entry.ref, offset: top - last.top } : undefined
}
export function restoreAnchor(geometry: Geometry, anchor: Anchor | undefined, viewportHeight = 0) {
  const item = anchor && geometry.byRef.get(anchor.ref)
  return item ? Math.max(0, Math.min(item.top + anchor!.offset, Math.max(0, geometry.height - viewportHeight))) : 0
}
